import React from 'react';
import { TomeSprig } from './TomeOrnaments';
import { TomeSkin, type TomeShapeProps } from './TomeSkin';
import { tomeCutVariant } from './tomeShape';
import { useTomeVibe } from './tomeVibeContext';

export type TomeTone = 'default' | 'ready' | 'available' | 'active' | 'completed' | 'locked' | 'quiet';

export interface TomeChapterProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /** The chapter rule's label; also the section's accessible name unless `aria-label` is given. */
  title: string;
  /** Optional count beside the label: a number, or a node such as "9 / 50". */
  count?: React.ReactNode;
  /** `ready` draws the rule in moss (second tier after a folio); `quiet` fades it (completed). */
  tone?: TomeTone;
  children?: React.ReactNode;
}

/** A chapter of the page: a small-caps rule with a sprig and count, then its entries. */
export const TomeChapter: React.FC<TomeChapterProps> = ({ title, count, tone = 'default', className = '', children, ...rest }) =>
  <section aria-label={title} {...rest} className={`tome-chapter ${className}`} data-tone={tone}>
    <h3 className="tome-chapter-title"><TomeSprig /><span className="tome-chapter-label">{title}</span>
      {count !== undefined && <span className="tome-chapter-count">{count}</span>}</h3>
    <div className="tome-chapter-body">{children}</div>
  </section>;

export interface TomeChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: TomeTone;
  /** `hand` (default) cuts the pill slightly unevenly, seeded by `shapeSeed` or the chip's text; `clean` is a true pill. */
  cut?: 'hand' | 'clean';
  shapeSeed?: string | number;
}

/** A status chip inked for parchment; one colour per tone. */
export const TomeChip: React.FC<TomeChipProps> = ({ tone = 'default', cut = 'hand', shapeSeed, className = '', ...rest }) =>
  <span {...rest} className={`tome-chip ${className}`} data-tone={tone}
    data-cut={cut === 'hand' ? tomeCutVariant(shapeSeed ?? (typeof rest.children === 'string' ? rest.children : tone)) : undefined} />;

export interface TomeEntryProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>, TomeShapeProps {
  /** Small line above the title, e.g. "Level 2". */
  kicker?: React.ReactNode;
  title: React.ReactNode;
  /** Right of the title, usually a `<TomeChip>`. */
  status?: React.ReactNode;
  /** Accent: the left rule and tint follow the tone. */
  tone?: TomeTone;
  /** `full` reads at full size; `compact` tightens padding and title (turn-ins, work in progress). */
  density?: 'full' | 'compact';
  children?: React.ReactNode;
}

/**
 * One entry on the page (an ordinary quest, a ware, a lesson): a light card
 * with a toned left rule and a faintly deckled edge (shape props as on
 * `TomePanel`; roughness follows the panel's vibe; seeded by `shapeSeed`, else
 * the title or accessible name).
 */
export const TomeEntry: React.FC<TomeEntryProps> = ({
  kicker, title, status, tone = 'default', density = 'full', className = '', children,
  edge = 'deckled', corners = 'round', roughness, shapeSeed, ...rest
}) => {
  const vibe = useTomeVibe();
  return <article {...rest} className={`tome-entry ${className}`} data-tone={tone} data-density={density}>
    <TomeSkin layer="entry" edge={edge} corners={corners} roughness={roughness ?? vibe.shape.roughness} texture="plain"
      shapeSeed={shapeSeed ?? (typeof title === 'string' ? title : rest['aria-label']) ?? 'entry'} />
    <header className="tome-entry-header">
      <div className="tome-entry-heading">
        {kicker && <span className="tome-entry-kicker">{kicker}</span>}
        <h4 className="tome-entry-title">{title}</h4>
      </div>
      {status}
    </header>
    {children}
  </article>;
};

export interface TomeRewardLineProps extends React.HTMLAttributes<HTMLElement> {
  /** Defaults to "Rewards". */
  label?: React.ReactNode;
  /** Inline facts on one line ("100 XP", "40 Gold", "New ability: Blink"). */
  parts: React.ReactNode[];
  /** Optional rows under the line (item icons and names). */
  items?: React.ReactNode;
}

/** What something pays: a labelled line of facts in ochre ink, then optional item rows. */
export const TomeRewardLine: React.FC<TomeRewardLineProps> = ({ label = 'Rewards', parts, items, className = '', ...rest }) =>
  <section {...rest} className={`tome-rewards ${className}`}>
    <span className="tome-rewards-label">{label}</span>
    {parts.length > 0 && <div className="tome-rewards-parts">
      {parts.map((part, index) => <span key={index} className="tome-rewards-part">{part}</span>)}
    </div>}
    {items}
  </section>;
